import React, { useCallback, useEffect, useMemo, useState } from "react";
import { collection, doc, getDocs, onSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Link, useSearchParams } from "react-router-dom";
import { db } from "../../firebase";
import { useUserContext } from "../../context/UserProvider";
import Spinner from "../util/Spinner";
import { TruckIcon } from "../util/Icons";

function isStoreVisible(product) {
  const metadataValue = product?.metadata?.store_visible ?? product?.stripe_metadata_store_visible;
  return metadataValue === true || String(metadataValue || "").trim().toLowerCase() === "true";
}

function isActive(data) {
  return data?.active !== false;
}

function isOneTimePrice(price) {
  return price?.type === "one_time" || !price?.recurring;
}

function getStoreSort(product) {
  const sortValue = product?.metadata?.store_sort ?? product?.stripe_metadata_store_sort;
  const sortNumber = Number(sortValue);
  return Number.isFinite(sortNumber) ? sortNumber : 9999;
}

function getExtraInfo(product) {
  return String(product?.metadata?.extra_info ?? product?.stripe_metadata_extra_info ?? "")
    .replace(/\\n/g, "\n")
    .trim();
}

function formatMoney(amount, currency = "cad") {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: String(currency || "cad").toUpperCase(),
  }).format(Number(amount || 0) / 100);
}

function getProductImage(product) {
  return Array.isArray(product?.images) ? product.images.find(Boolean) || "" : "";
}

function buildStoreItem(product, price) {
  return {
    productId: product.id,
    priceId: price.id,
    name: product.name || price.description || "Store item",
    description: product.description || price.description || "",
    extraInfo: getExtraInfo(product),
    image: getProductImage(product),
    unitAmount: Number(price.unit_amount || 0),
    currency: price.currency || "cad",
    sort: getStoreSort(product),
  };
}

export default function StorePage() {
  const { user } = useUserContext();
  const [searchParams] = useSearchParams();
  const [storeItems, setStoreItems] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const checkoutState = searchParams.get("checkout");
  const returnedOrderId = searchParams.get("orderId");

  useEffect(() => {
    let cancelled = false;

    async function loadStoreProducts() {
      setLoading(true);
      setErrorMessage("");

      try {
        const productsSnap = await getDocs(collection(db, "stripe_products"));
        const visibleProducts = productsSnap.docs
          .map((productDoc) => ({ id: productDoc.id, ...productDoc.data() }))
          .filter((product) => isActive(product) && isStoreVisible(product));

        const nextStoreItems = await Promise.all(
          visibleProducts.map(async (product) => {
            const pricesSnap = await getDocs(collection(db, "stripe_products", product.id, "prices"));
            const price = pricesSnap.docs
              .map((priceDoc) => ({ id: priceDoc.id, ...priceDoc.data() }))
              .filter((priceData) => isActive(priceData) && isOneTimePrice(priceData))
              .sort((a, b) => Number(a.unit_amount || 0) - Number(b.unit_amount || 0))[0];

            return price ? buildStoreItem(product, price) : null;
          })
        );

        if (!cancelled) {
          setStoreItems(
            nextStoreItems
              .filter(Boolean)
              .sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name))
          );
        }
      } catch (error) {
        console.error("Unable to load store products", error);
        if (!cancelled) {
          setErrorMessage("We couldn't load the store products. Please refresh and try again.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadStoreProducts();

    return () => {
      cancelled = true;
    };
  }, []);

  const selectedItems = useMemo(() => {
    return storeItems
      .map((item) => ({
        ...item,
        quantity: Number(quantities[item.priceId] || 0),
      }))
      .filter((item) => item.quantity > 0);
  }, [quantities, storeItems]);

  const subtotal = useMemo(() => {
    return selectedItems.reduce((total, item) => total + item.unitAmount * item.quantity, 0);
  }, [selectedItems]);

  const currency = selectedItems[0]?.currency || storeItems[0]?.currency || "cad";
  const itemCount = selectedItems.reduce((total, item) => total + item.quantity, 0);

  const updateQuantity = useCallback((priceId, nextQuantity) => {
    const normalizedQuantity = Math.max(0, Math.min(99, Number(nextQuantity) || 0));
    setQuantities((currentQuantities) => ({
      ...currentQuantities,
      [priceId]: normalizedQuantity,
    }));
  }, []);

  const startCheckout = useCallback(async () => {
    if (!user?.uid || selectedItems.length === 0 || checkoutLoading) {
      return;
    }

    setCheckoutLoading(true);
    setErrorMessage("");

    try {
      const createStoreCheckout = httpsCallable(getFunctions(), "createStoreCheckout");
      const { data } = await createStoreCheckout({
        items: selectedItems.map((item) => ({
          productId: item.productId,
          priceId: item.priceId,
          quantity: item.quantity,
        })),
        successUrl: `${window.location.origin}/store/confirmation`,
        cancelUrl: `${window.location.origin}/store`,
      });

      const orderId = data?.orderId;
      if (!orderId) {
        throw new Error("Store checkout did not return an order id.");
      }

      await new Promise((resolve, reject) => {
        let unsubscribe = () => {};
        const timeoutId = window.setTimeout(() => {
          unsubscribe();
          reject(new Error("Store checkout took too long to start. Please try again."));
        }, 30000);

        const checkoutRef = doc(db, "stripe_customers", user.uid, "checkout_sessions", orderId);
        unsubscribe = onSnapshot(
          checkoutRef,
          (snap) => {
            const checkoutData = snap.data() || {};
            if (checkoutData.error) {
              window.clearTimeout(timeoutId);
              unsubscribe();
              reject(new Error(checkoutData.error.message || "Store checkout failed."));
              return;
            }
            if (checkoutData.url) {
              window.clearTimeout(timeoutId);
              unsubscribe();
              window.location.assign(checkoutData.url);
              resolve();
            }
          },
          (error) => {
            window.clearTimeout(timeoutId);
            unsubscribe();
            reject(error);
          }
        );
      });
    } catch (error) {
      console.error("Unable to create store checkout", error);
      setErrorMessage(error?.message || "Unable to start checkout. Please try again.");
      setCheckoutLoading(false);
    }
  }, [checkoutLoading, selectedItems, user?.uid]);

  return (
    <div className="mx-auto mt-4 max-w-5xl pb-8">
      <div className="mb-4 flex flex-col gap-2 border-b border-slate-200 pb-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="mb-1 text-2xl font-bold text-slate-950">Store</h1>
          <p className="mb-0 text-sm text-slate-600">
            <span className="font-semibold text-emerald-700">Free Shipping.</span> Add extra items to ship with The Big Canada Run medal packages.
          </p>
        </div>
        <Link to="/" className="text-sm font-semibold text-blue-600 no-underline hover:text-blue-700">
          Back to home
        </Link>
      </div>

      {checkoutState === "success" && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          Store order received{returnedOrderId ? `: ${returnedOrderId}` : ""}.
        </div>
      )}

      {checkoutState === "cancelled" && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Checkout was cancelled. You can choose items again.
        </div>
      )}

      {errorMessage && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {errorMessage}
        </div>
      )}

      {loading ? (
        <div className="flex min-h-[240px] items-center justify-center">
          <Spinner />
        </div>
      ) : storeItems.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-8 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
            <TruckIcon size={28} />
          </div>
          <h2 className="mb-1 text-lg font-semibold text-slate-900">No store items are available yet.</h2>
          <p className="mb-0 text-sm text-slate-500">Products marked as store-visible in Stripe will appear here.</p>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr),320px]">
          <div className="grid gap-3 sm:grid-cols-2">
            {storeItems.map((item) => {
              const quantity = Number(quantities[item.priceId] || 0);
              return (
                <article key={item.priceId} className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                  <div className="grid grid-cols-[96px,minmax(0,1fr)] gap-3">
                    <div className="aspect-square overflow-hidden rounded-md bg-slate-100">
                      {item.image ? (
                        <img src={item.image} alt={item.name} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-slate-400">
                          <TruckIcon size={30} />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <h2 className="mb-1 text-base font-semibold leading-tight text-slate-950">{item.name}</h2>
                      {item.description && (
                        <p className="mb-0 line-clamp-3 text-sm leading-snug text-slate-500">{item.description}</p>
                      )}
                    </div>
                  </div>

                  {item.extraInfo && (
                    <p className="mt-3 mb-0 whitespace-pre-line rounded-md bg-slate-50 px-3 py-2 text-xs font-medium leading-snug text-slate-600">
                      {item.extraInfo}
                    </p>
                  )}

                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-slate-900">
                      {formatMoney(item.unitAmount, item.currency)}
                    </span>
                    <div className="grid h-9 grid-cols-[36px,44px,36px] items-center rounded-lg border border-slate-200">
                      <button
                        type="button"
                        className="h-full border-0 bg-transparent text-lg font-semibold text-slate-700 disabled:text-slate-300"
                        disabled={quantity === 0}
                        onClick={() => updateQuantity(item.priceId, quantity - 1)}
                        aria-label={`Remove one ${item.name}`}
                      >
                        -
                      </button>
                      <span className="text-center text-sm font-semibold text-slate-900">{quantity}</span>
                      <button
                        type="button"
                        className="h-full border-0 bg-transparent text-lg font-semibold text-slate-700"
                        onClick={() => updateQuantity(item.priceId, quantity + 1)}
                        aria-label={`Add one ${item.name}`}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          <aside className="h-fit rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-lg font-semibold text-slate-950">Cart</h2>
            {selectedItems.length === 0 ? (
              <p className="mb-0 text-sm text-slate-500">Choose quantities to start an order.</p>
            ) : (
              <div className="space-y-3">
                {selectedItems.map((item) => (
                  <div key={item.priceId} className="grid grid-cols-[1fr,auto] gap-3 text-sm">
                    <div className="min-w-0">
                      <p className="mb-0 truncate font-medium text-slate-800">{item.name}</p>
                      <p className="mb-0 text-slate-500">
                        {item.quantity} x {formatMoney(item.unitAmount, item.currency)}
                      </p>
                    </div>
                    <span className="font-semibold text-slate-900">
                      {formatMoney(item.unitAmount * item.quantity, item.currency)}
                    </span>
                  </div>
                ))}

                <div className="border-t border-slate-200 pt-3">
                  <div className="mb-3 flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-600">{itemCount} item{itemCount === 1 ? "" : "s"}</span>
                    <span className="text-lg font-bold text-slate-950">{formatMoney(subtotal, currency)}</span>
                  </div>
                  <div className="mb-3 flex items-center justify-between rounded-md bg-emerald-50 px-3 py-2 text-sm">
                    <span className="font-medium text-emerald-900">Shipping</span>
                    <span className="font-bold text-emerald-700">Free</span>
                  </div>
                  <button
                    type="button"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg border-0 bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:bg-slate-400"
                    disabled={checkoutLoading}
                    onClick={startCheckout}
                    aria-busy={checkoutLoading}
                  >
                    {checkoutLoading && (
                      <span className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" aria-hidden="true" />
                    )}
                    <span>{checkoutLoading ? "Starting checkout..." : "Checkout"}</span>
                  </button>
                </div>
              </div>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}
